import { useEffect, useMemo, useState } from "react";
import { AuthenticatedContext } from "./AuthenticatedContext";
import {
  LoadingState,
  Logout,
  LogoutOptions,
  UnauthenticatedState,
  UseAuth,
} from "../types/Auth";
import { z } from "zod";
import { jwtDecode } from "jwt-decode";
import { useTokenInvitationCtx } from "../../org/hooks/OrgInvite/useTokenInvitationCtx";
import { clearTokenProvider } from "../../api";

interface AuthMappedProviderProps {
  children: React.ReactNode;
  fallback: (state: UnauthenticatedState | LoadingState) => React.ReactNode;
  useAuth: () => UseAuth;
  setAccessToken: (token: string) => void;
  setTokenProvider: (provider: () => Promise<string>) => void;
  onLogout?: () => void;
  /**
   * Dev-only: append the AgenticProvisioner role to the decoded roles surfaced
   * to the app, leaving pacific_user_id and every other claim untouched. Only
   * the real-Auth0 path passes this; it is forced false in production builds.
   */
  addProvisionerRole?: boolean;
}

const customClaims = z.object({
  pacific_user_id: z.string(),
  pacific_roles: z.array(z.enum(["AgenticProvisioner", "AgenticUser", "user_admin", "users_readonly"])),
});

export function AuthMappedProvider({
  children,
  fallback,
  useAuth,
  setAccessToken,
  setTokenProvider,
  onLogout,
  addProvisionerRole = false,
}: AuthMappedProviderProps) {
  const {
    isAuthenticated,
    user,
    logout,
    loginWithRedirect,
    isLoading,
    error,
    getAccessTokenSilently,
  } = useAuth();

  const [localToken, setLocalToken] = useState<string | null>(null);
  const [tokenFetchFailed, setTokenFetchFailed] = useState(false);

  // Get invitation context at component level
  const { tokens } = useTokenInvitationCtx();

  const hasToken = localToken !== null;

  const logoutWithCallback: Logout = (options?: LogoutOptions) => {
    logout(options);
    if (onLogout) {
      onLogout();
    }
  };

  // Custom login function that preserves invitation tokens via Auth0 appState
  const loginWithInvitationState = async () => {
    // Get invitation token from in-memory store (URL was already cleaned up)
    const invitationToken = tokens.length > 0 ? tokens[0] : null;

    const appState = {
      // Full URL (origin + pathname + search + hash) so Auth0 can bounce back
      // to the same path-prefixed deployment after authenticating.
      returnTo: window.location.href,
      ...(invitationToken && { invitationToken }),
    };

    await loginWithRedirect({ appState });
  };

  useEffect(() => {
    const fetchToken = async () => {
      if (!isAuthenticated) {
        setLocalToken(null);
        setTokenFetchFailed(false);
        return;
      }

      // Register the token provider so every Axios request gets a fresh token.
      // Auth0's getAccessTokenSilently returns from its internal cache when valid
      // and transparently refreshes when the token is near expiry.
      setTokenProvider(getAccessTokenSilently);

      try {
        const token = await getAccessTokenSilently();
        setLocalToken(token);
        setAccessToken(token);
      } catch (error) {
        console.error("Error fetching token:", error);
        setLocalToken(null);
        setTokenFetchFailed(true);
      }
    };

    fetchToken();

    return () => {
      clearTokenProvider();
    };
  }, [isAuthenticated, getAccessTokenSilently, setAccessToken, setTokenProvider]);

  const decodedToken = useMemo(
    () => localToken && customClaims.parse(jwtDecode(localToken)),
    [localToken]
  );

  // Dev-only role injection: when enabled, surface AgenticProvisioner in addition to the real roles. 
  // The decoded token (incl. pacific_user_id) is never mutated — only the roles array exposed to the app is widened.
  const roles = useMemo(() => {
    if (!decodedToken) {
      return [];
    }
    if (
      addProvisionerRole &&
      !decodedToken.pacific_roles.includes("AgenticProvisioner")
    ) {
      return [...decodedToken.pacific_roles, "AgenticProvisioner" as const];
    }
    return decodedToken.pacific_roles;
  }, [decodedToken, addProvisionerRole]);

  if (isLoading) {
    return fallback({ state: "loading" });
  }

  if (!isAuthenticated || !user || tokenFetchFailed) {
    return fallback({
      state: "unauthenticated",
      loginWithRedirect: loginWithInvitationState,
      error,
    });
  }

  if (!decodedToken) {
    return fallback({ state: "loading" });
  }

  return (
    <AuthenticatedContext.Provider
      value={{
        state: "authenticated",
        user,
        userId: decodedToken.pacific_user_id,
        roles,
        hasToken,
        logout: logoutWithCallback,
        getAccessTokenSilently,
      }}
    >
      {children}
    </AuthenticatedContext.Provider>
  );
}
